import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, UserCog, Building2, User as UserIcon,
  X, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { authAPI, ImpersonatableCompany, ImpersonatableUser } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type ViewMode = 'grid' | 'table';
type CompanyTypeFilter = 'all' | 'customer' | 'supplier';

export default function LoginAsPage() {
  const navigate = useNavigate();
  const { user, startImpersonation } = useAuth();
  const currentUserEmail = user?.email;
  const [companies, setCompanies] = useState<ImpersonatableCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<ImpersonatableCompany[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyType, setSelectedCompanyType] = useState<CompanyTypeFilter>('all');
  const [_viewMode, _setViewMode] = useState<ViewMode>('grid');
  const [recentImpersonations, setRecentImpersonations] = useState<ImpersonatableUser[]>([]);

  // Check if user can access Login As (super admins + customer admins)
  const isHelixAdmin = user?.platform === 'HELIX' || user?.is_staff || user?.is_superuser || false;
  const isCustomerAdmin = (user?.is_company_admin || user?.can_manage_users) && user?.company_type !== 'supplier';
  const canImpersonate = isHelixAdmin || isCustomerAdmin || false;

  useEffect(() => {
    if (!canImpersonate) {
      navigate('/dashboard');
      return;
    }
    fetchImpersonatableUsers();
    loadRecentImpersonations();
  }, [canImpersonate, navigate]);

  useEffect(() => {
    filterCompanies();
  }, [searchTerm, companies, selectedCompanyType]);

  const fetchImpersonatableUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getImpersonatableUsers();
      setCompanies(response.data.data?.companies || response.data.companies || []);
    } catch (err: unknown) {
      console.error('Error fetching impersonatable users:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentImpersonations = () => {
    const recent = localStorage.getItem('recentImpersonations');
    if (recent) {
      try {
        const parsed = JSON.parse(recent);
        setRecentImpersonations(parsed.slice(0, 5)); // Keep only last 5
      } catch (e) {
        console.error('Error loading recent impersonations:', e);
      }
    }
  };

  const saveRecentImpersonation = (user: ImpersonatableUser) => {
    const recent = localStorage.getItem('recentImpersonations');
    let recentList: ImpersonatableUser[] = [];

    if (recent) {
      try {
        recentList = JSON.parse(recent);
      } catch (e) {
        console.error('Error parsing recent impersonations:', e);
      }
    }

    // Remove if already exists
    recentList = recentList.filter(u => u.user_id !== user.user_id);
    // Add to beginning
    recentList.unshift(user);
    // Keep only last 10
    recentList = recentList.slice(0, 10);

    localStorage.setItem('recentImpersonations', JSON.stringify(recentList));
    setRecentImpersonations(recentList.slice(0, 5));
  };

  const filterCompanies = () => {
    let filtered = companies;

    // Filter by company type
    if (selectedCompanyType !== 'all') {
      filtered = filtered.filter(c => c.company_type === selectedCompanyType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(company => {
        const companyMatch = company.company_name.toLowerCase().includes(search);
        const userMatch = company.users.some(user =>
          user.full_name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search) ||
          user.job_title?.toLowerCase().includes(search)
        );
        return companyMatch || userMatch;
      });
    }

    setFilteredCompanies(filtered);
  };

  const handleImpersonate = async (user: ImpersonatableUser) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.impersonate(user.user_id);

      // Save to recent
      saveRecentImpersonation(user);

      // API returns standardized format: { status, message, data: { target_user, target_token, original_token } }
      const impersonationData = response.data.data;

      // Start impersonation (triggers full page reload)
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        undefined,
        impersonationData.audit_log_id
      );
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to impersonate user');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const totalUsers = companies.reduce((sum, company) => sum + company.users.length, 0);
  const filteredUsers = filteredCompanies.reduce((sum, company) => sum + company.users.length, 0);

  if (!canImpersonate) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <UserCog className="h-8 w-8" />
          Login As User
        </h1>
        <p className="text-muted-foreground mt-1">
          Impersonate customer or supplier users to view their experience
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{companies.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {companies.filter(c => c.company_type === 'customer').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Suppliers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {companies.filter(c => c.company_type === 'supplier').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Impersonations */}
      {recentImpersonations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Recent Impersonations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {recentImpersonations.map((user) => (
                <Button
                  key={user.user_id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleImpersonate(user)}
                  disabled={loading}
                  className="gap-2"
                >
                  <UserIcon className="h-3 w-3" />
                  {user.full_name}
                  <span className="text-muted-foreground text-xs">({user.email})</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company name, user name, email, or job title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Company Type Filter */}
            <Select
              value={selectedCompanyType}
              onValueChange={(value) => setSelectedCompanyType(value as CompanyTypeFilter)}
            >
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Company Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="supplier">Suppliers</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh Button */}
            <Button
              variant="outline"
              onClick={fetchImpersonatableUsers}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredUsers} of {totalUsers} users
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        </CardContent>
      </Card>

      {/* Company and User List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              Loading users...
            </CardContent>
          </Card>
        ) : filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <p>No users found matching your filters</p>
              {searchTerm && (
                <Button
                  variant="link"
                  onClick={clearSearch}
                  className="mt-2"
                >
                  Clear search
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredCompanies.map((company) => (
            <Card key={company.company_id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-5 w-5" />
                    {company.company_name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={company.company_type === 'customer' ? 'default' : 'secondary'}
                    >
                      {company.company_type}
                    </Badge>
                    <Badge variant="outline">
                      {company.users.length} {company.users.length === 1 ? 'user' : 'users'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {company.users.map((user) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <UserIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{user.full_name}</div>
                          <div className="text-sm text-muted-foreground truncate">{user.email}</div>
                          {user.job_title && (
                            <div className="text-xs text-muted-foreground mt-1 truncate">
                              {user.job_title}
                              {user.role && ` · ${user.role}`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        {!user.is_active && (
                          <Badge variant="outline" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant={user.email === currentUserEmail ? 'outline' : 'default'}
                          onClick={() => handleImpersonate(user)}
                          disabled={!user.is_active || loading || user.email === currentUserEmail}
                        >
                          {user.email === currentUserEmail ? 'You' : 'Login As'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
